import { createContext } from "react";
import {
  OpensearchCuratedSearch,
  SearchAnalysisPreviewResults,
  OrgContactInternalSearchPreview,
  PreviousResultsInput,
} from "../__generated__/graphql";

export interface IAutopilotPreviewContactFeedback {
  isGoodFit?: boolean;
  isBadFit?: boolean;
  feedbackDetails?: {
    wrongPositionLevel?: boolean;
    wrongCompanyType?: boolean;
    missingRequiredSkills?: boolean;
    wrongRole?: boolean;
    otherReason?: boolean;
    additionalFeedback?: string;
  };
}

export interface IScoringCriteriaResult {
  criterion_fit?: boolean | null;
  reasoning?: string | null;
  criterion_id?: number | null;
}

export interface IPreviousResultsWithFeedback {
  contact: {
    firstName?: string | null;
    lastName?: string | null;
    title?: string | null;
    companyName?: string | null;
    rationale?: string | null;
    scoringCriteriaResults?: IScoringCriteriaResult[] | null;
  };
  userFeedbackScore?: number | null;
  userFeedbackComment?: string | null;
}

export interface IInternalAutopilotHydrationValues {
  prompt?: string;
  savedFilters?: OpensearchCuratedSearch | null;
  expandFilters?: OpensearchCuratedSearch | null;
  expandPrompt?: string;
  scoringCriteriaValues?: string[];
  expandScoringCriteriaValues?: string[];
  specificCompanies?: string[];
  curatedSearchId?: number | null;
  savedAnalysisPreview?: SearchAnalysisPreviewResults | null;
  savedPreviews?: OrgContactInternalSearchPreview | null;
}

export type PreviewContactFeedbackMap = Map<number, PreviousResultsInput>;

interface IInternalAutopilotHydrationContext {
  prompt: string;
  setPrompt: (prompt: string) => void;
  expandPrompt: string;
  setExpandPrompt: (prompt: string) => void;
  savedFilters: OpensearchCuratedSearch | null;
  setSavedFilters: (filters: OpensearchCuratedSearch | null) => void;
  scoringCriteriaValues: string[];
  setScoringCriteriaValues: (filtering: string[]) => void;
  curatedSearchId: number | null;
  setCuratedSearchId: (id: number | null) => void;
  specificCompanies: string[];
  setSpecificCompanies: (companies: string[]) => void;
  savedAnalysisPreview: SearchAnalysisPreviewResults | null;
  setSavedAnalysisPreview: (
    preview: SearchAnalysisPreviewResults | null,
  ) => void;
  savedPreviews: OrgContactInternalSearchPreview | null;
  setSavedPreviews: (previews: OrgContactInternalSearchPreview | null) => void;
  resetHydrationValues: (values: IInternalAutopilotHydrationValues) => void;
}

const InternalAutopilotHydrationContext =
  createContext<IInternalAutopilotHydrationContext>({
    prompt: "",
    setPrompt: (_prompt: string) => {},
    expandPrompt: "",
    setExpandPrompt: (_prompt: string) => {},
    savedFilters: null,
    setSavedFilters: (_filters: OpensearchCuratedSearch | null) => {},
    scoringCriteriaValues: [] as string[],
    setScoringCriteriaValues: (_filtering: string[]) => {},
    curatedSearchId: null,
    setCuratedSearchId: (_id: number | null) => {},
    specificCompanies: [],
    setSpecificCompanies: () => {},
    savedAnalysisPreview: null,
    setSavedAnalysisPreview: (
      _preview: SearchAnalysisPreviewResults | null,
    ) => {},
    savedPreviews: null,
    setSavedPreviews: (_previews: OrgContactInternalSearchPreview | null) => {},
    resetHydrationValues: () => {},
  });

export default InternalAutopilotHydrationContext;
